import { useEffect, useState } from "react";
import { Loader2, Plus, Save, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableMultiSelect } from "@/components/ui/searchable-select";
import { Textarea } from "@/components/ui/textarea";
import {
  useCategories, useCreateCategory, useCreateIndicator, useUpdateIndicator,
  type IndicatorInput,
} from "@/hooks/useIndicators";
import { PERIOD_HINT, PERIOD_LABEL } from "@/lib/indicator-period";
import { cn } from "@/lib/utils";
import type { IndicatorDirection, IndicatorPeriod, IndicatorRow, WorkflowState } from "@/lib/types";
import type { Visibility } from "@/hooks/useDocumentMutations";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pass an existing row for edit mode, omit for create. */
  indicator?: IndicatorRow | null;
}

/** Create / edit dialog. CEO+ only (the API rejects others with 403). */
export function IndicatorFormDialog({ open, onOpenChange, indicator }: Props) {
  const isEdit = !!indicator;
  const create = useCreateIndicator();
  const update = useUpdateIndicator();
  const mutation = isEdit ? update : create;

  const [name, setName]               = useState("");
  const [unit, setUnit]               = useState("");
  const [description, setDescription] = useState("");
  const [direction, setDirection]     = useState<IndicatorDirection>("increasing");
  const [period, setPeriod]           = useState<IndicatorPeriod>("quarterly");
  const [categories, setCategories]   = useState<string[]>([]);
  const [visibility, setVisibility]   = useState<Visibility>("staff");
  const categoryList = useCategories();
  const createCategory = useCreateCategory();

  // Inline "create a new category" UX — toggle + draft state. Keeps the
  // user in the indicator dialog so they don't have to navigate away.
  const [showNewCat,  setShowNewCat]  = useState(false);
  const [newCatName,  setNewCatName]  = useState("");
  const [newCatError, setNewCatError] = useState<string | null>(null);

  function onCreateCategory(e: React.FormEvent) {
    e.preventDefault();
    const name = newCatName.trim();
    if (!name) return;
    setNewCatError(null);
    createCategory.mutate(name, {
      onSuccess: (cat) => {
        // Auto-select the freshly-created category so the user's "I
        // need this one for this indicator" intent flows through.
        setCategories((prev) => [...prev, cat.id]);
        setNewCatName("");
        setShowNewCat(false);
      },
      onError: (err) => {
        const e = err as { response?: { data?: { name?: string[]; detail?: string } } };
        setNewCatError(e?.response?.data?.name?.[0] || e?.response?.data?.detail || "Couldn't create that category.");
      },
    });
  }

  useEffect(() => {
    if (!open) return;
    if (indicator) {
      setName(indicator.name);
      setUnit(indicator.unit || "");
      setDescription("");        // list shape doesn't carry description
      setDirection(indicator.direction);
      setPeriod(indicator.period);
      setCategories(indicator.categories || []);
      setVisibility(visibilityFromState(indicator.workflow_state));
    } else {
      setName(""); setUnit(""); setDescription("");
      setDirection("increasing"); setPeriod("quarterly");
      setCategories([]); setVisibility("staff");
    }
    create.reset();
    update.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, indicator?.id]);

  const canSubmit =
    name.trim().length > 0
    && categories.length > 0   // backend enforces; mirror in the UI
    && !mutation.isPending;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const input: IndicatorInput = {
      name:        name.trim(),
      unit:        unit.trim() || undefined,
      description: description.trim() || undefined,
      direction,
      period,
      categories,
      visibility,
    };
    const handlers = { onSuccess: () => onOpenChange(false) };
    if (isEdit && indicator) update.mutate({ id: indicator.id, input }, handlers);
    else                     create.mutate(input, handlers);
  }

  const err = mutation.error as ApiError;
  const errorMsg = formatApiError(err);

  return (
    <Dialog open={open} onOpenChange={(v) => !mutation.isPending && onOpenChange(v)}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit “${indicator?.name}”` : "New indicator"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update the indicator's metadata. To log a new observation, use the “Add value” button on the indicator's page."
              : "Create the indicator first, then add observations from its page."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="ind-name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="ind-name" required maxLength={200}
                value={name} onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Renewable energy share"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ind-unit">Unit</Label>
              <Input
                id="ind-unit" maxLength={40}
                value={unit} onChange={(e) => setUnit(e.target.value)}
                placeholder='e.g. %, USD, tonnes'
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ind-direction">Direction</Label>
              <select
                id="ind-direction"
                value={direction}
                onChange={(e) => setDirection(e.target.value as IndicatorDirection)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm font-medium"
              >
                <option value="increasing">Higher is better</option>
                <option value="decreasing">Lower is better</option>
                <option value="stable">Stable target</option>
              </select>
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <div className="flex items-center justify-between">
                <Label>
                  Categories <span className="text-destructive">*</span>
                </Label>
                {/* Inline "+ New" toggle — opens a small one-input form
                    right below so the user can create a missing category
                    without leaving the indicator dialog. */}
                {!showNewCat && (
                  <button
                    type="button"
                    onClick={() => { setShowNewCat(true); setNewCatError(null); }}
                    className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-primary transition hover:bg-primary/10"
                  >
                    <Plus className="h-3 w-3" />
                    New category
                  </button>
                )}
              </div>

              <SearchableMultiSelect<string>
                values={categories}
                onChange={setCategories}
                options={(categoryList.data ?? []).map((c) => ({
                  value: c.id, label: c.name,
                }))}
                emptyHint={categoryList.isLoading
                  ? "Loading…"
                  : 'No categories yet — click "New category" above to add the first one.'}
              />

              {/* Inline create form — collapsed by default, slides in
                  under the multi-select when the user wants to add one. */}
              {showNewCat && (
                <form
                  onSubmit={onCreateCategory}
                  className="flex flex-wrap items-center gap-2 rounded-md border border-primary/30 bg-primary/5 p-2"
                >
                  <Input
                    autoFocus
                    value={newCatName}
                    onChange={(e) => setNewCatName(e.target.value)}
                    placeholder="e.g. Environmental"
                    disabled={createCategory.isPending}
                    maxLength={300}
                    className="h-8 min-w-[12rem] flex-1"
                  />
                  <Button
                    type="submit" size="sm" className="h-8"
                    disabled={!newCatName.trim() || createCategory.isPending}
                  >
                    {createCategory.isPending
                      ? <><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />Adding…</>
                      : <><Plus className="mr-1 h-3.5 w-3.5" />Add</>}
                  </Button>
                  <Button
                    type="button" variant="ghost" size="sm" className="h-8"
                    disabled={createCategory.isPending}
                    onClick={() => { setShowNewCat(false); setNewCatName(""); setNewCatError(null); }}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                  {newCatError && (
                    <p className="basis-full text-xs text-destructive">{newCatError}</p>
                  )}
                </form>
              )}

              <p className="text-xs text-muted-foreground">
                Required. An indicator can belong to several themes
                (e.g. "Environmental" + "Economic"). Use the
                <span className="mx-1 font-medium">+ New category</span> link
                above to add a fresh one — it auto-selects when saved.
              </p>
              {categories.length === 0 && (
                <p className="text-xs text-destructive">
                  Pick at least one category before saving.
                </p>
              )}
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="ind-period">Measurement period</Label>
              <select
                id="ind-period"
                value={period}
                onChange={(e) => setPeriod(e.target.value as IndicatorPeriod)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm font-medium"
              >
                {(["annual","quarterly","monthly","daily"] as IndicatorPeriod[]).map((p) => (
                  <option key={p} value={p}>{PERIOD_LABEL[p]}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">{PERIOD_HINT[period]}</p>
              {isEdit && indicator && indicator.period !== period && (
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  Changing the period after observations exist may snap existing dates to the new period start — only do it when the indicator has just been created.
                </p>
              )}
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="ind-desc">Description</Label>
              <Textarea
                id="ind-desc" rows={3}
                value={description} onChange={(e) => setDescription(e.target.value)}
                placeholder={isEdit ? "Leave blank to keep the existing description." : "What does this measure? Why does it matter?"}
              />
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label>Who can see this indicator?</Label>
              <div className="grid gap-2">
                <VisibilityOption
                  value="private" current={visibility} onChange={setVisibility}
                  label="Only me"
                  hint="Private to you. Other staff and directors won't see it."
                />
                <VisibilityOption
                  value="staff" current={visibility} onChange={setVisibility}
                  label="Staff portal"
                  hint="Visible to every signed-in colleague immediately."
                />
                <VisibilityOption
                  value="public" current={visibility} onChange={setVisibility}
                  label="Public portal"
                  hint="Live on the public site immediately."
                />
              </div>
            </div>
          </div>

          {errorMsg && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {errorMsg}
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button type="button" variant="ghost"
              onClick={() => onOpenChange(false)} disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {mutation.isPending
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{isEdit ? "Saving…" : "Creating…"}</>
                : <><Save className="mr-2 h-4 w-4" />{isEdit ? "Save changes" : "Create indicator"}</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function VisibilityOption({
  value, current, onChange, label, hint,
}: {
  value: Visibility;
  current: Visibility;
  onChange: (v: Visibility) => void;
  label: string;
  hint: string;
}) {
  const checked = current === value;
  return (
    <label className={cn(
      "flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors",
      checked ? "border-primary/40 bg-primary/5" : "border-border hover:bg-accent/30",
    )}>
      <input
        type="radio" name="ind-visibility" value={value}
        checked={checked} onChange={() => onChange(value)}
        className="mt-1 h-4 w-4 accent-primary"
      />
      <div className="flex-1 text-sm">
        <div className="font-medium">{label}</div>
        <div className="text-xs text-muted-foreground">{hint}</div>
      </div>
    </label>
  );
}

function visibilityFromState(state: WorkflowState): Visibility {
  if (state === "owner_only")   return "private";
  if (state === "published")    return "public";
  return "staff";
}

type ApiError = { response?: { data?: Record<string, unknown> | { detail?: string } } } | undefined;
function formatApiError(err: ApiError): string | null {
  if (!err?.response?.data) return null;
  const data = err.response.data as Record<string, unknown> & { detail?: string };
  if (typeof data.detail === "string") return data.detail;
  for (const [field, msgs] of Object.entries(data)) {
    if (Array.isArray(msgs) && msgs.length && typeof msgs[0] === "string") {
      return `${field}: ${msgs[0]}`;
    }
  }
  return "Save failed.";
}
